import { calculateBundlePricing } from 'sidecar-utilities'

/** Care estimate interface matching the structure from calculateBundlePricing */
export interface CareEstimate {
  savings: number
  billOverBenefitAmount: number
  estimatedMemberResponsibility: number
  netEstimatedMemberResponsibility?: number
  earnedBenefit: number
  appliedToDeductible: number
  providerPrice: number
  netEarnedBenefit?: number
}

/** Practice interface matching the structure from premember */
export interface Practice {
  locationUuid: string
  facilityName: string
  facilityNpi?: string
  doctorCount?: number
  price: number
  careEstimate: CareEstimate
  averageGamAppropriatenessScore?: number
  addresses: Array<{
    street: string
    city: string
    state: string
    zip: string
    lon: number
    lat: number
    primary: boolean
    facilityName?: string
  }>
  contractType?: 'DIRECT_BILL' | 'CASH_PAY'
  bookmarked?: boolean
}

/** Address interface for the transformed provider */
export interface ProviderAddress {
  uuid: string
  street: string
  city: string
  state: string
  zip: string
  lon: number
  lat: number
  phone: string
  primary: boolean
  facilityName: string
}

/** Practice location interface for the transformed provider */
export interface PracticeLocation {
  organizationNpi: string
  locationUuid: string
  facilityName: string
  phone: string
  street: string
  city: string
  state: string
  zipcode: string
  lat: number
  lon: number
  price: number
  careEstimate: CareEstimate
  contractType?: 'DIRECT_BILL' | 'CASH_PAY'
}

/** Transformed provider interface */
export interface TransformedProvider {
  npi: number | null
  firstName: string
  lastName: string
  gender: string
  organizationName: string
  type: string
  contractType?: 'DIRECT_BILL' | 'CASH_PAY'
  rating: number
  reviewCount: number
  addresses: ProviderAddress[]
  taxonomies: any[]
  languages: any[]
  bookmarked?: boolean
  pricing: {
    type: 'pay' | 'earn' | 'hsa'
    amount: number
    sublabel?: string
  }
  youllPay: number
  youllEarn: number
  careEstimate: CareEstimate
  providerRate: number
  practiceLocations: PracticeLocation[]
  gamAppropriatenessScore?: number
}

/**
 * Transforms practice data into provider format for UI consistency
 *
 * @param practice - Practice data from API
 * @returns Transformed provider object
 */
export function transformPracticeToProvider(
  practice: Practice,
): TransformedProvider {
  // Create addresses array from practice addresses
  const addresses: ProviderAddress[] = practice.addresses.map(addr => ({
    uuid: practice.locationUuid,
    street: addr.street,
    city: addr.city,
    state: addr.state,
    zip: addr.zip,
    lon: addr.lon,
    lat: addr.lat,
    phone: '',
    primary: addr.primary,
    facilityName: practice.facilityName,
  }))

  // Use shared utility for consistent pricing calculation
  const pricingResult = calculateBundlePricing(practice.careEstimate)

  // Create practice locations to match the expected format
  const practiceLocations: PracticeLocation[] = practice.addresses.map(
    addr => ({
      organizationNpi: practice.locationUuid,
      locationUuid: practice.locationUuid,
      facilityName: practice.facilityName || addr.facilityName || '',
      phone: '',
      street: addr.street,
      city: addr.city,
      state: addr.state,
      zipcode: addr.zip,
      lat: addr.lat,
      lon: addr.lon,
      price: practice.price,
      careEstimate: practice.careEstimate,
      contractType: practice.contractType,
    }),
  )

  // Parse facilityNpi if available, otherwise null (no fake fallback)
  const parsedNpi = parseInt(practice?.facilityNpi || '', 10)
  const npi = Number.isNaN(parsedNpi) ? null : parsedNpi

  // Create a provider object from practice data with all required properties
  return {
    npi,
    firstName: '',
    lastName: '',
    gender: '',
    organizationName: practice.facilityName,
    type: 'ORGANIZATION',
    rating: 0,
    reviewCount: practice.doctorCount || 0,
    addresses,
    taxonomies: [],
    languages: [],
    bookmarked: practice.bookmarked,
    contractType: practice.contractType,
    pricing: pricingResult.pricing,
    youllPay: pricingResult.youllPay,
    youllEarn: pricingResult.youllEarn,
    careEstimate: practice.careEstimate,
    providerRate: practice.price,
    practiceLocations,
    gamAppropriatenessScore: practice?.averageGamAppropriatenessScore,
  }
}
